import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { PURGE } from "redux-persist";
import { fetchAppointmentDetailsTimeslots } from "../../../../shared/hooks/useFetchAppointment";
import API_URLS from "../../Utils/axr";
import { EC_DW_LOGGING_STEPS } from "../../../../shared/utils/Constants";
import store from "../store";
import DwLogutil from "../../Utils/DwLogutil";
import { errorConditionGTM } from "../../Utils/googleTags";
import { v4 as uuidv4 } from "uuid";

type moversAppointmentTimeslotStateType = {
  moversAppointmentTimeslotData: any;
  confirmAvailableSlotData: any;
  selectedSlotData: any;
  disconnectDate: any;
  moveOrderNumber: string | null;
  confirmAvailableSlotLoading: boolean;
  confirmAvailableSlotError: any;
  selectedDateTime: any;
  selectedDateData: any;
  loading: boolean;
  refreshing: boolean;
  moverOrderFailed: boolean;
  error: any;
  ows: boolean;
};

const initialState: moversAppointmentTimeslotStateType = {
  moversAppointmentTimeslotData: null,
  confirmAvailableSlotData: null,
  selectedSlotData: null,
  disconnectDate: null,
  moveOrderNumber: null,
  confirmAvailableSlotLoading: false,
  confirmAvailableSlotError: null,
  loading: false,
  refreshing: false,
  error: null,
  selectedDateTime: null,
  selectedDateData: null,
  moverOrderFailed: false,
  ows: false,
};


export const confirmAvailableSlot = createAsyncThunk(
  "appointment/confirmAvailableSlot",
  async (appointmentData: any, { rejectWithValue }) => {
    try {
      const reduxStore = store.getState();
      const headers = reduxStore?.brightspeedToken?.headers;
      const user = reduxStore?.user?.data;
      
      const addressQualification =
        reduxStore?.addressQualification?.moversData?.moversAddressQualification;

      const moversAppointmentTimeslotData =reduxStore?.getMoversAppointmentTimeslots?.moversAppointmentTimeslotData;
      const inquiryReservationNumber =reduxStore?.getMoversAppointmentTimeslots?.moversAppointmentTimeslotData?.schedulingResponse?.inquiryReservationNumber;
      const moversEligibleUser = reduxStore?.brightSpeedUserUtils?.moversEligibleUser;

      // find selected slot
      const selectedSlot = moversAppointmentTimeslotData?.schedulingResponse?.potentialAppointments?.find(
        (item: any) => {
          const slot = `${item.appointmentTime.appointmentAfterTime} - ${item.appointmentTime.appointmentBeforeTime}`;

          return (
            item.appointmentDate === appointmentData?.date &&
            slot === appointmentData?.timeSlot
          );
        }
      );

      let url = API_URLS["confirmAvailableSlot"];

      const transactionId =
        addressQualification?.entity?.partnerSessionId ||
        uuidv4().slice(0, 21);

      const payload = {
        baseline: {
          transactionId,
          partnerId: "",
          salesCode: "",
          partnerReferenceId: "",
          agentId: "",
          channel: moversEligibleUser ? "ContactCenter" : "web-acm",
          siteId: headers?.siteId?.toLowerCase() || "",
          wirecenter:
            addressQualification?.entity?.wireCenter || "",
          intent: "",
          workflow: "mover",
          billingType: reduxStore?.user?.data?.billingType || "",
        },
        inquiryReservationNumber: inquiryReservationNumber || "",
        selectedAppointment: {
          appointmentDate: selectedSlot?.appointmentDate,
          appointmentTime: {
            appointmentAfterTime:
              selectedSlot?.appointmentTime?.appointmentAfterTime,
            appointmentBeforeTime:
              selectedSlot?.appointmentTime?.appointmentBeforeTime,
            appointmentCompleteTime:
              selectedSlot?.appointmentTime?.appointmentCompleteTime,
          },
        },
        installType: "Schedule",
      };

      const params = { "": url };

      DwLogutil({stepName: EC_DW_LOGGING_STEPS.movers_schedule_install_attempted});

      const response = await fetchAppointmentDetailsTimeslots(
        params,
        payload,
        headers
      );

      DwLogutil({stepName: EC_DW_LOGGING_STEPS.movers_schedule_install_success});

      return {
        apiResponse: response?.data,
        selectedSlot: selectedSlot
      };
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        "Something went wrong while confirming available slot";
      
      DwLogutil({stepName: EC_DW_LOGGING_STEPS.movers_schedule_install_failed});

      return rejectWithValue(errorMessage);
    }
  }
);

export const getMoversAppointmentTimeslots = createAsyncThunk(
  "moversAppointment/timeslots",
  async (_, { rejectWithValue }) => {
    
    try {
      const reduxStore = store.getState();
      const headers = reduxStore?.brightspeedToken?.headers;
      const user = reduxStore?.user?.data;
      const moversSelectedAddress = reduxStore?.addressQualification?.selectedMoversAddress;
      const addressQualification = reduxStore?.addressQualification?.moversData?.moversAddressQualification;          

      const { lookup } = await import("zipcode-to-timezone");
      const lookupRes = lookup(moversSelectedAddress?.zipcode);

      const { format, addDays, addBusinessDays, max, startOfDay } = await import("date-fns");

      const pendingDisconnectOrderDueDate = addressQualification?.entity?.pendingDisconnectOrderDueDate;
      const interval = addressQualification?.entity?.interval;
      const existingService = addressQualification?.entity?.existingService;
      const pendingDisconnectOrderExists = addressQualification?.entity?.pendingDisconnectOrderExists;
      let isOws = false;

      /** Same MM/DD/YYYY parsing as ScheduleInstallation for qualification dates */
      const parsePendingDisconnectDueDate = (dateStr: any): Date | null => {
        if (dateStr == null || dateStr === "") return null;
        if (typeof dateStr === "string" && dateStr.includes("/")) {
          const parts = dateStr.split("/").map(Number);
          const [month, day, year] = parts;
          if (!month || !day || !year) return null;
          return new Date(year, month - 1, day);
        }
        const d = new Date(dateStr);
        return Number.isNaN(d.getTime()) ? null : d;
      };

      const getNextDayFromPendingDisconnect = (dueDateStr: any) => {
        const today = new Date();
        const parsed = parsePendingDisconnectDueDate(dueDateStr);
        let baseDate;

        if (parsed) {
          baseDate = parsed > today ? parsed : today;
        } else {
          baseDate = today;
        }

        return addDays(baseDate, 1);
      };

      const getSearchWindowStartDate = (): Date => {
        const todayStart = startOfDay(new Date());
        // OWS Logic
        if (interval !== null && interval === 0 && !pendingDisconnectOrderExists &&
          existingService && pendingDisconnectOrderDueDate === null) {
          isOws = true;          
          const todayPlusInterval = addBusinessDays(todayStart, 5);
          return todayPlusInterval;
        }
        // OWS Logic
        if (pendingDisconnectOrderDueDate && interval === 0) {
          return getNextDayFromPendingDisconnect(pendingDisconnectOrderDueDate);
        }
        if (interval !== null && interval >= 0) {
          const todayPlusInterval = addDays(todayStart, interval);
          const dueParsed = parsePendingDisconnectDueDate(
            pendingDisconnectOrderDueDate
          );
          if (dueParsed) {
            const dueStart = startOfDay(dueParsed);
            return max([dueStart, todayPlusInterval]);
          }
          return todayPlusInterval;
        }

        return getNextDayFromPendingDisconnect(pendingDisconnectOrderDueDate);
      };

      const searchWindowStart = getSearchWindowStartDate();
      const startDate = format(searchWindowStart, "yyyy-MM-dd");
      const endDate = format(addDays(searchWindowStart, 60), "yyyy-MM-dd");      

      const payload = {
        baseline: {
          transactionId:
            addressQualification?.entity?.partnerSessionId ||
            uuidv4().slice(0, 21),
          channel: "web-acm",
          wirecenter:
            addressQualification?.entity?.wireCenter ||
            "MANTNCXA",
          siteId: headers?.siteId?.toLowerCase(),
          workflow: "mover",
        },
        jobLocation: {
          street: moversSelectedAddress?.street_line,
          city: moversSelectedAddress?.city,
          state: moversSelectedAddress?.state,
          zipCode: moversSelectedAddress?.zipcode,
          country:
            addressQualification?.entity?.country || "USA",
          geoLocation: {
            longitude:
              addressQualification?.entity?.longitude,
            latitude:
              addressQualification?.entity?.latitude,
          },
        },
        customerInfo: {
          customerName: `${user?.firstName || ""} ${user?.lastName || ""}`,
          phoneNumber: user?.contactNumber || "",
          timeZone:
            lookupRes === "America/Indiana/Indianapolis"
              ? "America/New_York"
              : lookupRes === "America/Detroit"
              ? "America/New_York"
              : lookupRes || "America/New_York",
        },
        appointment: {
          searchWindowStart: startDate + "T00:00:00",
          searchWindowEnd: endDate + "T00:00:00",
        },
      };

      const params = { "": API_URLS?.appointmentDetailsUrlForCTL };

      DwLogutil({stepName: EC_DW_LOGGING_STEPS.movers_available_slot_attempted});
      const response = await fetchAppointmentDetailsTimeslots(
        params,
        payload,
        headers
      );

      DwLogutil({
        stepName: response?.data?.messageStatus[0]?.statusCode == 200
          ?  EC_DW_LOGGING_STEPS.movers_available_slot_success
          :  EC_DW_LOGGING_STEPS.movers_available_slot_failed,
        error:
          response?.data?.length === 0 ? "Response data is empty" : undefined,
      });
      DwLogutil({stepName: EC_DW_LOGGING_STEPS.movers_available_slot_success});
      if(isOws){
        DwLogutil({stepName: EC_DW_LOGGING_STEPS.movers_address_ows});
      }
      return {
        data: response?.data || response,
        ows: isOws,
      };
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        "Something went wrong while fetching appointment timeslots";
     
      DwLogutil({stepName: EC_DW_LOGGING_STEPS.movers_available_slot_failed,  error: errorMessage,});

      return rejectWithValue(errorMessage);
    }
  }
);



const moversAppointmentTimeslotSlice = createSlice({
  name: "moversAppointmentTimeslot",
  initialState,
  reducers: {
    clearAppointmentTimeslotSlice: () => {
      return initialState;
    },
    setDisconnectDate: (state, action: PayloadAction<string>) => {
      state.disconnectDate = action.payload;
    },
    setSelectedDateData: (state, action: PayloadAction<any>) => {
      state.selectedDateData = action.payload;
    },
    setSelectedDateTime: (state, action: PayloadAction<any>) => {
      state.selectedDateTime = action.payload;
    },
    setSelectedSlotData: (state, action: PayloadAction<any>) => {
      state.selectedSlotData = action.payload;
    },
    setMoveOrderNumber: (state, action: PayloadAction<string | null>) => {
      state.moveOrderNumber = action.payload;
    },
    setMoveOrderFailed: (state, action: PayloadAction<boolean>) => {
      state.moverOrderFailed = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMoversAppointmentTimeslots.pending, (state) => {
        if (!state.moversAppointmentTimeslotData) {
          state.loading = true;
        }
        else{
          state.refreshing = true;
        }
        state.error = null;
      })
      .addCase(getMoversAppointmentTimeslots.fulfilled, (state, action) => {
        state.loading = false;
        state.refreshing = false;
        state.moversAppointmentTimeslotData = action.payload?.data;
        state.ows = Boolean(action.payload?.ows);
        state.error = null;
      })
      .addCase(getMoversAppointmentTimeslots.rejected, (state, action) => {
        state.loading = false;
        state.refreshing = false;
        state.moversAppointmentTimeslotData = null;
        state.error = action.payload;
      })

      .addCase(confirmAvailableSlot.pending, (state) => {
        state.confirmAvailableSlotLoading = true;
        state.confirmAvailableSlotError = null;
      })
      .addCase(confirmAvailableSlot.fulfilled, (state, action) => {
        state.confirmAvailableSlotLoading = false;
        state.confirmAvailableSlotData = action.payload?.apiResponse;
        state.selectedSlotData = action.payload?.selectedSlot;
        state.confirmAvailableSlotError = null;
      })
      .addCase(confirmAvailableSlot.rejected, (state, action) => {
        state.confirmAvailableSlotLoading = false;
        state.confirmAvailableSlotData = null;
        state.selectedSlotData = null;
        state.confirmAvailableSlotError =
          action.payload ?? action.error?.message;
      });

    builder.addCase(PURGE, () => {
      return initialState;
    });
  },
});

export const { clearAppointmentTimeslotSlice, setDisconnectDate, setSelectedSlotData, setMoveOrderNumber, setMoveOrderFailed, setSelectedDateData, setSelectedDateTime } =
  moversAppointmentTimeslotSlice.actions;

export default moversAppointmentTimeslotSlice.reducer;